import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { priceFormatter } from '../../helpers/formatters';
import { scenarioActions } from '../../scenarios/scenarioSlice';

/** @type {import('@/types').Market} */
const defaultMarketValues = {
  description: '',
  buy_sell: 'placeholder',
  price: 0,
  base_price: 0,
  max_volume: 0,
};

export function PopupMarkets({ pointId }) {
  const componentType = 'markets';
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);

  /**
   * @type {import('../scenarios/scenarioSlice').ScenarioState}
   */
  const {
    data: { markets: allMarkets },
    isEditing,
  } = useSelector((state) => state.scenario);
  const markets = allMarkets.filter((market) => market.point == pointId);

  /** @type {import('@/types').State<import('@/types').Market>} */
  const [newMarket, setNewMarket] = useState({
    ...defaultMarketValues,
    point: pointId,
  });

  const resetNewMarket = () => {
    setNewMarket({ ...defaultMarketValues, point: pointId });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMarket({ ...newMarket, [name]: value });
  };

  const handleSubmit = () => {
    dispatch(
      scenarioActions.updateComponent({ componentType, component: newMarket }),
    );
    resetNewMarket();
    setIsAdding(false);
    return false;
  };

  const handleCancel = () => {
    resetNewMarket();
    setIsAdding(false);
    return false;
  };

  const isValid = useMemo(() => {
    if (newMarket.buy_sell === 'placeholder') {
      return false;
    }
    return true;
  }, [newMarket.buy_sell]);

  if (isEditing !== true) {
    return null;
  }

  return (
    <div className="my-1" style={{ minWidth: 300 }}>
      {isAdding ? (
        <h5>New Market</h5>
      ) : (
        <div className="d-flex justify-content-between">
          <h5>Markets</h5>
          <button
            className="btn btn-sm btn-success mb-1"
            onClick={() => {
              setIsAdding(true);
              return false;
            }}
          >
            Add Market
          </button>
        </div>
      )}
      {isAdding ? (
        <>
          <div className="form-group my-2">
            <label
              className="form-label mt-2"
              htmlFor={`${pointId}-description`}
            >
              Description
            </label>
            <input
              className="form-control"
              id={`${pointId}-description`}
              name="description"
              type="text"
              value={newMarket.description}
              onChange={handleChange}
            />

            <label className="form-label mt-2" htmlFor={`${pointId}-buy-sell`}>
              Buy/Sell
            </label>
            <select
              className="form-control"
              id={`${pointId}-buy-sell`}
              name="buy_sell"
              value={newMarket.buy_sell}
              onChange={handleChange}
            >
              <option value="placeholder" disabled>
                Select
              </option>
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
              <option value="BOTH">Both</option>
            </select>

            <label className="form-label mt-2" htmlFor={`${pointId}-price`}>
              Price
            </label>
            <input
              className="form-control"
              id={`${pointId}-price`}
              name="price"
              type="number"
              value={newMarket.base_price}
              onChange={(e) => {
                handleChange(e);
                handleChange({
                  ...e,
                  target: { ...e.target, name: 'base_price' },
                });
              }}
            />

            <label
              className="form-label mt-2"
              htmlFor={`${pointId}-max-volume`}
            >
              Volume
            </label>
            <input
              className="form-control"
              id={`${pointId}-max-volume`}
              name="max-volume"
              type="number"
              value={newMarket.max_volume}
              onChange={(e) => {
                handleChange(e);
                handleChange({
                  ...e,
                  target: { ...e.target, name: 'max_volume' },
                });
              }}
            />
          </div>
          <div className="d-flex">
            <button className="btn btn-danger me-1 mb-1" onClick={handleCancel}>
              Cancel
            </button>
            <button
              className="btn btn-success mb-1"
              onClick={handleSubmit}
              disabled={isValid === false}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Buy/Sell</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {markets.length > 0 ? (
              markets.map((market) => (
                <tr key={`${pointId}-${market.id}`}>
                  <td>{market.buy_sell}</td>
                  <td>
                    {market.price_component
                      ? market.price
                      : priceFormatter.format(market.base_price)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2" className="text-center">
                  No Markets
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
